import React from 'react';
import type { SyncBriefResponse } from '../../api/syncTrackingApi';
import { briefCategoryView, timeAgo } from '../../lib/syncTrackingMeta';

const SparkleIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /><path d="M18.5 16.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
    </svg>
);

const RefreshIcon = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 11a8 8 0 1 0-2.3 5.7" /><path d="M20 4v7h-7" />
    </svg>
);

const CloseIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
        <path d="M6 6l12 12M18 6L6 18" />
    </svg>
);

const ActionIcon = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
);

interface Props {
    brief: SyncBriefResponse | null;
    loading: boolean;
    error: string | null;
    onRefresh: () => void;
    onClose: () => void;
}

/**
 * Gemini's read on the location's most common sync failures.
 *
 * Sits between the summary tiles and the filter bar: the tiles say how many
 * records need attention, this says why. Always describes every failing record
 * for the practice, not just the current page or filter.
 */
export const AIBriefPanel: React.FC<Props> = ({ brief, loading, error, onRefresh, onClose }) => (
    <div className="border-[1.5px] border-mapper-teal-border bg-mapper-teal-bg-2 rounded-[14px] mb-[22px] overflow-hidden">
        <div className="flex items-start justify-between gap-4 px-[18px] pt-[15px] pb-[13px]">
            <div className="flex items-center gap-[10px] min-w-0">
                <span className="w-[26px] h-[26px] rounded-[8px] shrink-0 flex items-center justify-center bg-mapper-teal text-white shadow-mapper-btn">
                    {SparkleIcon}
                </span>
                <div className="min-w-0">
                    <div className="text-[14.5px] font-extrabold tracking-[-0.2px] leading-tight">AI Brief</div>
                    <div className="text-[11.5px] text-mapper-muted-2">
                        {loading
                            ? 'Analysing every failing record for this practice…'
                            : brief
                                ? `${brief.total_failing} failing ${brief.total_failing === 1 ? 'record' : 'records'} · ${brief.distinct_codes} error ${brief.distinct_codes === 1 ? 'type' : 'types'}${brief.llm_model ? ` · ${brief.llm_model}` : ''}`
                                : 'Most common sync errors, explained'}
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
                <button
                    onClick={onRefresh}
                    disabled={loading}
                    title="Re-analyse"
                    className="flex items-center gap-[6px] px-[10px] py-[6px] rounded-[8px] text-[12px] font-semibold text-mapper-teal-hover hover:bg-mapper-teal-bg-3 disabled:opacity-40"
                >
                    {RefreshIcon}
                    <span>Re-analyse</span>
                </button>
                <button
                    onClick={onClose}
                    title="Hide brief"
                    className="w-[26px] h-[26px] flex items-center justify-center rounded-[8px] text-mapper-muted-3 hover:bg-mapper-teal-bg-3 hover:text-mapper-body-text"
                >
                    {CloseIcon}
                </button>
            </div>
        </div>

        <div className="bg-mapper-white border-t border-mapper-teal-border px-[18px] py-4">
            {loading && <BriefSkeleton />}

            {!loading && error && (
                <div className="text-[13px] text-mapper-red">
                    {error}{' '}
                    <button onClick={onRefresh} className="underline font-semibold text-mapper-teal-hover">Try again</button>
                </div>
            )}

            {!loading && !error && brief && (
                <>
                    {brief.headline && (
                        <div className="text-[14.5px] font-bold tracking-[-0.2px] mb-[6px]">{brief.headline}</div>
                    )}
                    {brief.summary && (
                        <p className="text-[13.5px] text-mapper-body-text leading-[1.55] mb-[14px] max-w-[860px]">{brief.summary}</p>
                    )}

                    {brief.themes.length > 0 && (
                        <div className="grid grid-cols-2 gap-[10px] mb-[14px]">
                            {brief.themes.map((t, i) => {
                                const cv = briefCategoryView(t.category);
                                return (
                                    <div key={i} className="border border-mapper-border rounded-[11px] bg-mapper-surface px-[14px] py-[12px]">
                                        <div className="flex items-center justify-between gap-3 mb-[7px]">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className={`px-[7px] py-[2px] rounded-[6px] text-[10.5px] font-bold tracking-[0.2px] shrink-0 ${cv.pill}`}>
                                                    {cv.label.toUpperCase()}
                                                </span>
                                                <span className="text-[13px] font-bold truncate">{t.title}</span>
                                            </div>
                                            <span className="font-mono text-[13px] font-bold text-mapper-body-text-3 shrink-0">
                                                {t.affected_count}
                                            </span>
                                        </div>
                                        {t.explanation && (
                                            <p className="text-[12.5px] text-mapper-muted leading-[1.5] mb-[9px]">{t.explanation}</p>
                                        )}
                                        {t.recommended_action && (
                                            <div className="flex items-start gap-[6px] text-[12.5px] text-mapper-teal-hover font-semibold leading-[1.45]">
                                                <span className="mt-[3px] shrink-0">{ActionIcon}</span>
                                                <span>{t.recommended_action}</span>
                                            </div>
                                        )}
                                        <div className="flex flex-wrap gap-[5px] mt-[10px]">
                                            {t.failure_codes.map(code => (
                                                <span key={code} className="font-mono text-[10.5px] px-[6px] py-[2px] rounded-[5px] bg-mapper-gray-bg text-mapper-gray-text">
                                                    {code}
                                                </span>
                                            ))}
                                            {t.self_healing && (
                                                <span className="text-[10.5px] px-[6px] py-[2px] rounded-[5px] bg-mapper-blue-bg text-mapper-blue font-semibold">
                                                    self-healing
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {brief.next_steps.length > 0 && (
                        <div className="border border-mapper-teal-border-2 bg-mapper-teal-bg rounded-[11px] px-[14px] py-[11px] mb-[14px]">
                            <div className="text-[11px] font-bold tracking-[0.4px] text-mapper-teal-hover mb-[7px]">DO THIS NEXT</div>
                            <ol className="flex flex-col gap-[5px]">
                                {brief.next_steps.map((s, i) => (
                                    <li key={i} className="flex items-start gap-[8px] text-[13px] text-mapper-body-text-3 leading-[1.5]">
                                        <span className="font-mono text-[11.5px] font-bold text-mapper-teal shrink-0 mt-[2px]">{i + 1}</span>
                                        <span>{s}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}

                    {brief.top_codes.length > 0 && (
                        <div>
                            <div className="text-[11px] font-bold tracking-[0.4px] text-mapper-muted-3 mb-[7px]">
                                MOST COMMON ERRORS
                            </div>
                            <div className="flex flex-col">
                                {brief.top_codes.slice(0, 6).map(c => (
                                    <div key={c.failure_code} className="flex items-center gap-3 py-[7px] border-b border-mapper-border-4 last:border-b-0">
                                        <span className="font-mono text-[12px] text-mapper-body-text-3 flex-1 min-w-0 truncate">{c.failure_code}</span>
                                        {c.exhausted_retries > 0 && (
                                            <span className="text-[10.5px] px-[6px] py-[2px] rounded-[5px] bg-mapper-red-bg text-mapper-red font-semibold shrink-0">
                                                {c.exhausted_retries} out of retries
                                            </span>
                                        )}
                                        <span className="text-[11.5px] text-mapper-muted-2 w-[74px] text-right shrink-0">{timeAgo(c.last_seen)}</span>
                                        <span className="font-mono text-[12.5px] font-bold w-[36px] text-right shrink-0">{c.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <p className="text-[11px] text-mapper-muted-3 mt-[14px]">
                        Counts come straight from your sync records; the AI only groups and explains them. Always sanity-check before making changes.
                    </p>
                </>
            )}
        </div>
    </div>
);

const BriefSkeleton: React.FC = () => (
    <div className="animate-pulse">
        <div className="h-[15px] w-[62%] rounded-[5px] bg-mapper-border-3 mb-[9px]" />
        <div className="h-[12px] w-[88%] rounded-[5px] bg-mapper-border-4 mb-[5px]" />
        <div className="h-[12px] w-[74%] rounded-[5px] bg-mapper-border-4 mb-[16px]" />
        <div className="grid grid-cols-2 gap-[10px]">
            {[0, 1].map(i => (
                <div key={i} className="border border-mapper-border rounded-[11px] bg-mapper-surface px-[14px] py-[12px]">
                    <div className="h-[13px] w-[55%] rounded-[5px] bg-mapper-border-3 mb-[9px]" />
                    <div className="h-[11px] w-full rounded-[5px] bg-mapper-border-4 mb-[5px]" />
                    <div className="h-[11px] w-[70%] rounded-[5px] bg-mapper-border-4" />
                </div>
            ))}
        </div>
    </div>
);
